import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Tooltip,
  Divider,
  Badge,
  Tabs,
  Alert
} from "antd";
import {
  BankOutlined,
  PlusOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const HotelPaymentsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [hotelSummary, setHotelSummary] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Modals
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [addHotelModalVisible, setAddHotelModalVisible] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);

  const [ledgerModalVisible, setLedgerModalVisible] = useState(false);
  const [cancellationModalVisible, setCancellationModalVisible] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  const [paymentForm] = Form.useForm();
  const [cancellationForm] = Form.useForm();
  const [addHotelForm] = Form.useForm();

  useEffect(() => {
    fetchHotelSummary();
    fetchPaymentHistory();
  }, []);

  const fetchHotelSummary = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/hotel-payments/summary");
      const list = res.data?.data || [];
      setHotelSummary(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching hotel payment summary:", err);
      setHotelSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await apiClient.get("/hotel-payments/payments");
      const list = res.data?.data || [];
      setPaymentHistory(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching hotel payment history:", err);
      setPaymentHistory([]);
    }
  };

  // Metrics
  const totalPayableCost = hotelSummary.reduce((sum, h) => sum + Number(h.totalEffectivePayable || 0), 0);
  const totalPaidToHotels = hotelSummary.reduce((sum, h) => sum + Number(h.totalPaid || 0), 0);
  const totalOutstanding = totalPayableCost - totalPaidToHotels;

  // Handle Log Payment to Hotel
  const handleLogPayment = async (values) => {
    try {
      const payload = {
        hotelName: values.hotelName || selectedHotel?.hotelName,
        bookingReference: values.bookingReference || "",
        amount: Number(values.amount),
        paymentDate: values.paymentDate ? values.paymentDate.toDate() : new Date(),
        paymentMode: values.paymentMode || "bank_transfer",
        utrNumber: values.utrNumber || "",
        notes: values.notes || ""
      };

      await apiClient.post("/hotel-payments/payments", payload);
      message.success(`Payment of ₹${values.amount.toLocaleString()} logged to ${payload.hotelName}`);

      setPaymentModalVisible(false);
      paymentForm.resetFields();
      setSelectedHotel(null);

      fetchHotelSummary();
      fetchPaymentHistory();
    } catch (err) {
      console.error("Failed to log hotel payment:", err);
      message.error("Failed to record hotel payment");
    }
  };

  // Handle Update Cancellation Charge
  const handleUpdateCancellationCharge = async (values) => {
    try {
      if (!selectedReservation) return;

      await apiClient.patch("/hotel-payments/cancellation-charge", {
        bookingId: selectedReservation.bookingId,
        hotelName: selectedHotel?.hotelName,
        cancellationFee: Number(values.cancellationFee || 0),
        waivedReason: values.waivedReason || ""
      });

      message.success("Hotel cancellation charge updated!");
      setCancellationModalVisible(false);
      setSelectedReservation(null);
      cancellationForm.resetFields();

      fetchHotelSummary();
    } catch (err) {
      console.error("Failed to update cancellation charge:", err);
      message.error("Failed to update cancellation charge");
    }
  };

  // Handle Create Hotel Account / Vendor Profile
  const handleCreateHotelAccount = async (values) => {
    try {
      await apiClient.post("/hotel-payments/hotels", values);
      message.success(`Hotel Account "${values.name}" registered successfully with bank details!`);
      setAddHotelModalVisible(false);
      addHotelForm.resetFields();
      fetchHotelSummary();
    } catch (err) {
      console.error("Failed to create hotel account:", err);
      message.error(err.response?.data?.message || "Failed to create hotel account");
    }
  };

  const hotelColumns = [
    {
      title: "Hotel Name",
      dataIndex: "hotelName",
      key: "hotelName",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{name}</div>
          {record.city && <Tag color="blue" style={{ fontSize: 10, marginTop: 2 }}>{record.city}</Tag>}
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {record.totalBookingsCount} Stays ({record.activeBookingsCount} Active
            {record.cancelledBookingsCount > 0 ? `, ${record.cancelledBookingsCount} Cancelled` : ""})
          </div>
        </div>
      )
    },
    {
      title: "Bank & Settlement Details",
      key: "bankDetails",
      render: (_, record) => {
        const b = record.bankDetails || {};
        const hasBank = b.bankName || b.accountNumber || b.upiId;
        if (!hasBank) {
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              No bank details added
            </Text>
          );
        }
        return (
          <div style={{ fontSize: 11, background: "#f8fafc", padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
            {b.bankName && <div><strong>Bank:</strong> {b.bankName}</div>}
            {b.accountNumber && <div><strong>A/C:</strong> {b.accountNumber} {b.ifscCode ? `(IFSC: ${b.ifscCode})` : ""}</div>}
            {b.accountHolderName && <div><strong>Name:</strong> {b.accountHolderName}</div>}
            {b.upiId && <div><strong>UPI:</strong> {b.upiId}</div>}
            {b.gstin && <div><strong>GSTIN:</strong> {b.gstin}</div>}
          </div>
        );
      }
    },
    {
      title: "Total Payable Cost (₹)",
      dataIndex: "totalEffectivePayable",
      key: "totalEffectivePayable",
      render: (val, record) => (
        <div>
          <span style={{ fontWeight: 600 }}>₹{Number(val || 0).toLocaleString()}</span>
          {record.cancelledBookingsCount > 0 && (
            <Tooltip title="Includes custom negotiated cancellation fees for cancelled trips">
              <InfoCircleOutlined style={{ marginLeft: 4, color: "#f59e0b" }} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: "Total Paid (₹)",
      dataIndex: "totalPaid",
      key: "totalPaid",
      render: (val) => (
        <span style={{ fontWeight: 600, color: "#059669" }}>
          ₹{Number(val || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: "Outstanding Balance (₹)",
      dataIndex: "outstandingBalance",
      key: "outstandingBalance",
      render: (val) => {
        const isDue = val > 0;
        return (
          <Tag color={isDue ? "red" : "green"} style={{ fontWeight: 700, fontSize: 13, padding: "2px 8px" }}>
            ₹{Math.max(0, Number(val || 0)).toLocaleString()} {isDue ? "OUTSTANDING" : "PAID CLEAR"}
          </Tag>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<DollarCircleOutlined />}
            onClick={() => {
              setSelectedHotel(record);
              paymentForm.setFieldsValue({
                hotelName: record.hotelName,
                amount: Math.max(0, record.outstandingBalance),
                paymentMode: "bank_transfer"
              });
              setPaymentModalVisible(true);
            }}
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
          >
            Log Payment
          </Button>

          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => {
              setSelectedHotel(record);
              setLedgerModalVisible(true);
            }}
          >
            Hotel Ledger & Stays
          </Button>
        </Space>
      )
    }
  ];

  const reservationColumns = [
    {
      title: "Lead Ref / Customer",
      key: "customer",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.customerName}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>Ref: {record.leadId || "N/A"}</div>
        </div>
      )
    },
    {
      title: "Stay Dates",
      key: "dates",
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          {record.checkIn ? dayjs(record.checkIn).format("DD MMM YYYY") : "N/A"} -{" "}
          {record.checkOut ? dayjs(record.checkOut).format("DD MMM YYYY") : "N/A"}
          <div style={{ color: "#64748b" }}>{record.nights} Night(s)</div>
        </div>
      )
    },
    {
      title: "Trip Status",
      dataIndex: "bookingStatus",
      key: "bookingStatus",
      render: (st) => (
        <Tag color={st === "cancelled" ? "red" : st === "completed" ? "blue" : "green"}>
          {(st || "CONFIRMED").toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Original Cost",
      dataIndex: "originalCost",
      key: "originalCost",
      render: (val) => <span>₹{Number(val || 0).toLocaleString()}</span>
    },
    {
      title: "Effective Payable",
      key: "effectivePayable",
      render: (_, record) => {
        const isCancelled = record.bookingStatus === "cancelled";
        return (
          <div>
            <div style={{ fontWeight: 700, color: isCancelled ? "#dc2626" : "#059669" }}>
              ₹{Number(record.effectivePayable || 0).toLocaleString()}
            </div>
            {isCancelled && (
              <Tag color="orange" style={{ marginTop: 2, fontSize: 10 }}>
                {record.cancellationFee === 0 ? "Fee Waived (₹0)" : `Fee: ₹${record.cancellationFee}`}
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setSelectedReservation(record);
            cancellationForm.setFieldsValue({
              cancellationFee: record.cancellationFee ?? 0,
              waivedReason: record.waivedReason || "Negotiated waiver / Service issue"
            });
            setCancellationModalVisible(true);
          }}
        >
          Adjust Fee
        </Button>
      )
    }
  ];

  const paymentHistoryColumns = [
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date) => dayjs(date).format("DD MMM YYYY, hh:mm A")
    },
    {
      title: "Hotel Name",
      dataIndex: "hotelName",
      key: "hotelName",
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>
    },
    {
      title: "Amount Paid (₹)",
      dataIndex: "amount",
      key: "amount",
      render: (val) => <span style={{ fontWeight: 700, color: "#059669" }}>₹{Number(val || 0).toLocaleString()}</span>
    },
    {
      title: "Mode & UTR",
      key: "mode",
      render: (_, record) => (
        <div>
          <Tag color="purple">{String(record.paymentMode || "bank_transfer").toUpperCase()}</Tag>
          {record.utrNumber && <div style={{ fontSize: 11, color: "#64748b" }}>UTR: {record.utrNumber}</div>}
        </div>
      )
    },
    {
      title: "Booking Ref / Notes",
      key: "notes",
      render: (_, record) => (
        <div style={{ fontSize: 12, color: "#475569" }}>
          {record.bookingReference && <div>Ref: {record.bookingReference}</div>}
          {record.notes && <div>{record.notes}</div>}
        </div>
      )
    }
  ];

  return (
    <div className="hotel-payments-management">
      {/* Alert Policy Banner */}
      <Alert
        message={<span style={{ fontWeight: 700 }}>Hotel Outstanding & Cancellation Control Logic</span>}
        description={
          <div style={{ fontSize: 12, lineHeight: "1.6" }}>
            <strong>Multi-Destination & Interrupted Trips:</strong> When a trip has multiple hotel stayovers and gets cancelled prior to check-in or during the trip, Admin & Accounts team can manually communicate with each hotel partner and enter the exact negotiated cancellation charge (e.g. ₹0 if waived due to service issues or 1-day prior notice). Hotel Outstanding is calculated dynamically based on effective payable charges minus total logged disbursements.
          </div>
        }
        type="info"
        showIcon
        icon={<SafetyCertificateOutlined />}
        style={{ marginBottom: 20, borderRadius: 8 }}
      />

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13 }}>Total Hotel Liabilities (Effective Cost)</span>}
              value={totalPayableCost}
              prefix={<BankOutlined style={{ color: "#4f46e5" }} />}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: "#1e293b" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13 }}>Total Paid / Disbursed to Hotels</span>}
              value={totalPaidToHotels}
              prefix={<CheckCircleOutlined style={{ color: "#10b981" }} />}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: "#10b981" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13 }}>Net Hotel Outstanding Balance</span>}
              value={totalOutstanding}
              prefix={<ClockCircleOutlined style={{ color: "#ef4444" }} />}
              formatter={(val) => `₹${Math.max(0, Number(val)).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: totalOutstanding > 0 ? "#ef4444" : "#10b981" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Action Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Hotel Outstanding Balances & Payment Ledger</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Track vendor payments, register hotel bank accounts, log disbursements, and manage cancellation charges per hotel</Text>
        </div>

        <Space wrap>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={() => {
              addHotelForm.resetFields();
              setAddHotelModalVisible(true);
            }}
            style={{ borderColor: "#4f46e5", color: "#4f46e5", fontWeight: 600 }}
          >
            Add Hotel Vendor Account
          </Button>

          <Button
            type="primary"
            icon={<DollarCircleOutlined />}
            onClick={() => {
              setSelectedHotel(null);
              paymentForm.resetFields();
              setPaymentModalVisible(true);
            }}
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
          >
            Log Hotel Payment
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 12 }}>
        <Tabs
          items={[
            {
              key: "summary",
              label: (
                <span>
                  <BankOutlined /> Hotel Outstanding Summary ({hotelSummary.length})
                </span>
              ),
              children: (
                <Table
                  columns={hotelColumns}
                  dataSource={hotelSummary}
                  rowKey="hotelName"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 800 }}
                />
              )
            },
            {
              key: "history",
              label: (
                <span>
                  <HistoryOutlined /> Payment Disbursement Logs ({paymentHistory.length})
                </span>
              ),
              children: (
                <Table
                  columns={paymentHistoryColumns}
                  dataSource={paymentHistory}
                  rowKey="_id"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 800 }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* MODAL 1: Log Payment to Hotel */}
      <Modal
        title={`Log Payment Disbursed to Hotel ${selectedHotel ? `- ${selectedHotel.hotelName}` : ""}`}
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={550}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Form form={paymentForm} layout="vertical" onFinish={handleLogPayment}>
          <Form.Item
            name="hotelName"
            label="Hotel Name"
            rules={[{ required: true, message: "Hotel name is required" }]}
          >
            <Input placeholder="e.g. Grand Hyatt Goa" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="amount" label="Amount Paid (₹)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={1} placeholder="15000" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="paymentDate" label="Payment Date" initialValue={dayjs()}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="paymentMode" label="Payment Mode" initialValue="bank_transfer">
                <Select>
                  <Option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</Option>
                  <Option value="upi">UPI Payment</Option>
                  <Option value="cheque">Cheque</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="credit_card">Company Credit Card</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="utrNumber" label="UTR / Transaction Ref No.">
                <Input placeholder="UTR12345678" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="bookingReference" label="Booking / Trip Ref (Optional)">
            <Input placeholder="e.g. BK-1002" />
          </Form.Item>

          <Form.Item name="notes" label="Notes / Remarks">
            <TextArea rows={2} placeholder="Optional payment description..." />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => setPaymentModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#059669", borderColor: "#059669" }}>
              Confirm Payment Disbursement
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL 2: Hotel Stays & Reservations Breakdown */}
      <Modal
        title={`Hotel Ledger & Reservation Stays - ${selectedHotel?.hotelName}`}
        open={ledgerModalVisible}
        onCancel={() => setLedgerModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLedgerModalVisible(false)}>
            Close
          </Button>
        ]}
        width={850}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Table
          columns={reservationColumns}
          dataSource={selectedHotel?.reservations || []}
          rowKey={(r, i) => `${r.bookingId}_${i}`}
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
        />
      </Modal>

      {/* MODAL 3: Manage Hotel Cancellation Charge Override */}
      <Modal
        title="Adjust Negotiated Hotel Cancellation Charge"
        open={cancellationModalVisible}
        onCancel={() => setCancellationModalVisible(false)}
        footer={null}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Form form={cancellationForm} layout="vertical" onFinish={handleUpdateCancellationCharge}>
          <Alert
            message="Flexible Cancellation Control"
            description="If a hotel waives cancellation fees due to service issues, 1-day prior notice, or partner negotiations, enter ₹0. The updated charge will directly update the hotel outstanding balance."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item name="cancellationFee" label="Negotiated Cancellation Fee Charged by Hotel (₹)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
          </Form.Item>

          <Form.Item name="waivedReason" label="Reason for Waiver / Fee Adjustment">
            <TextArea rows={2} placeholder="e.g. Hotel waived 100% fee due to medical emergency / service issue" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => setCancellationModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#4f46e5", borderColor: "#4f46e5" }}>
              Save Cancellation Fee
            </Button>
          </div>
        </Form>
      </Modal>
      {/* MODAL 4: Add New Hotel Account / Vendor Profile */}
      <Modal
        title="Register New Hotel Vendor Account & Bank Details"
        open={addHotelModalVisible}
        onCancel={() => setAddHotelModalVisible(false)}
        footer={null}
        width={650}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Form form={addHotelForm} layout="vertical" onFinish={handleCreateHotelAccount}>
          <Alert
            message="Hotel Vendor Account Information"
            description="Add vendor details and bank account numbers for seamless disbursement of hotel payments."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item name="name" label="Hotel Name" rules={[{ required: true, message: "Hotel name is required" }]}>
                <Input placeholder="e.g. Taj Lake Palace Udaipur" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item name="starRating" label="Star Rating" initialValue={4}>
                <Select>
                  <Option value={3}>3 Star</Option>
                  <Option value={4}>4 Star</Option>
                  <Option value={5}>5 Star Luxury</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="city" label="City" rules={[{ required: true, message: "City is required" }]}>
                <Input placeholder="e.g. Udaipur" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="state" label="State">
                <Input placeholder="e.g. Rajasthan" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>Hotel Contact Person</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="contactPerson" label="Contact Person">
                <Input placeholder="Manager Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="phone" label="Phone / WhatsApp">
                <Input placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="email" label="Email">
                <Input placeholder="reservations@hotel.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>Bank Account & Settlement Details</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="bankName" label="Bank Name">
                <Input placeholder="e.g. HDFC Bank / ICICI Bank" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="accountHolderName" label="Account Beneficiary Name">
                <Input placeholder="e.g. Taj Hotels Private Limited" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="accountNumber" label="Account Number">
                <Input placeholder="50100234567890" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="ifscCode" label="IFSC Code">
                <Input placeholder="HDFC0001234" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="upiId" label="UPI ID (VPA)">
                <Input placeholder="hotel@hdfcbank" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="gstin" label="GSTIN Number">
                <Input placeholder="08AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
            <Button onClick={() => setAddHotelModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#4f46e5", borderColor: "#4f46e5" }}>
              Save Hotel Account
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default HotelPaymentsManagement;
